<template>
  <transition
    name="theme-fade"
    mode="out-in"
  >
    <div
      :key="isDarkMode ? 'dark' : 'light'"
      :class="{ dark: isDarkMode }"
    >
      <div
        class="auth-wrapper"
        :class="{ dark: isDarkMode }"
      >
        <div class="fixed top-4 right-4 z-10">
          <Button
            plain
            severity="secondary"
            size="lg"
            :icon="isDarkMode ? 'sun-bright' : 'moon'"
            aria-label="Toggle dark mode"
            @click="toggleTheme"
          />
        </div>

        <div class="auth-card sm:w-full md:w-[400px]">
          <slot />
        </div>
      </div>

      <div class="gradient-bg">
        <svg xmlns="http://www.w3.org/2000/svg">
          <defs>
            <filter id="goo">
              <feGaussianBlur
                in="SourceGraphic"
                stdDeviation="10"
                result="blur"
              />
              <feColorMatrix
                in="blur"
                mode="matrix"
                values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -8"
                result="goo"
              />
              <feBlend
                in="SourceGraphic"
                in2="goo"
              />
            </filter>
          </defs>
        </svg>
        <div class="gradients-container">
          <div class="g1"></div>
          <div class="g2"></div>
          <div class="g3"></div>
          <div class="g4"></div>
          <div class="g5"></div>
          <div
            ref="backgroundInteractive"
            class="interactive"
          ></div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
// components
import Button from '@/components/button/Button.vue'

// stores
import { useUserStore } from '@/stores/UserStore'

// composables
import { computed, onMounted, ref } from 'vue'

/** Pinia user store instance */
const userStore = useUserStore()

/** Background interactive element reference */
const backgroundInteractive = ref<HTMLElement | null>(null)

/**
 * Indicates whether dark mode is currently enabled based on the
 * `userSettings.darkmode` flag in the user store.
 */
const isDarkMode = computed<boolean>(() => userStore.userSettings.darkmode === 'dark')

/**
 * Toggle theme between light and dark without calling the API.
 * - Updates Pinia `userStore.userSettings.darkmode`
 * - Persists to localStorage key `userSettingsLK`
 */
function toggleTheme(): void {
  try {
    const next = isDarkMode.value ? 'light' : 'dark'
    userStore.userSettings = {
      ...(userStore.userSettings || {}),
      darkmode: next
    }

    try {
      const current = localStorage.getItem('userSettingsLK')
      const obj = current ? JSON.parse(current) : {}
      obj.darkmode = next
      localStorage.setItem('userSettingsLK', JSON.stringify(obj))
    } catch (e) {
      console.error('Failed to persist theme locally', e)
    }
  } catch (error) {
    console.error('Failed to toggle theme', error)
  }
}

onMounted(() => {
  try {
    let currentX = 0
    let currentY = 0
    let targetX = 0
    let targetY = 0

    function move(): void {
      try {
        currentX += (targetX - currentX) / 20
        currentY += (targetY - currentY) / 20
        if (backgroundInteractive.value) {
          backgroundInteractive.value.style.transform = `translate(${Math.round(currentX)}px, ${Math.round(currentY)}px)`
        }
        requestAnimationFrame(() => move())
      } catch (err) {
        console.error('Animation tick failed', err)
      }
    }

    window.addEventListener('mousemove', (event: MouseEvent) => {
      try {
        targetX = event.clientX
        targetY = event.clientY
      } catch (err) {
        console.error('Mouse move handler failed', err)
      }
    })

    move()
  } catch (error) {
    console.error('Failed mounting background interaction', error)
  }
})
</script>

<style scoped>
.theme-fade-enter-from,
.theme-fade-leave-to {
  opacity: 0;
  filter: blur(6px);
}
.theme-fade-enter-active,
.theme-fade-leave-active {
  transition:
    opacity 250ms ease,
    filter 250ms ease;
}

.gradient-bg {
  --color-bg1: rgb(8, 12, 34); /* deep navy */
  --color-bg2: rgb(25, 0, 38); /* deep purple */
  --color1: 30, 120, 214; /* royal blue glow */
  --color2: 155, 90, 245; /* violet glow */
  --color3: 64, 180, 255; /* cyan highlight */
  --color4: 120, 40, 160; /* magenta-purple accent */
  --color5: 90, 30, 120; /* large soft purple */
  --color-interactive: 100, 120, 255; /* interactive blue */
  --circle-size: 80%;
  --blending: hard-light;
}
.dark {
  --color-bg1: rgb(8, 12, 34); /* deep navy */
  --color-bg2: rgb(25, 0, 38); /* deep purple */
  --color1: 30, 120, 214; /* royal blue glow */
  --color2: 155, 90, 245; /* violet glow */
  --color3: 64, 180, 255; /* cyan highlight */
  --color4: 120, 40, 160; /* magenta-purple accent */
  --color5: 90, 30, 120; /* large soft purple */
  --color-interactive: 100, 120, 255; /* interactive blue */
}
.auth-wrapper {
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
}

.auth-card {
  background: rgba(var(--bg-primary_alt-rgb), 0.8);
  min-width: 400px;
  max-width: 500px;
  margin: var(--s-4) 0rem;
  padding: var(--s-8) var(--s-12) var(--s-8) var(--s-12);
  box-shadow: var(--shadow-md);
  border-radius: var(--rounded-3xl);
  z-index: 1;
  box-shadow: 0px 10px 10px 10px rgba(0, 0, 0, 0.04);
  position: relative;
}

.dark .auth-card {
  background: rgba(var(--bg-primary_alt-rgb), 0.8);
}

@keyframes moveInCircle {
  0% {
    transform: rotate(0deg);
  }
  50% {
    transform: rotate(180deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes moveVertical {
  0% {
    transform: translateY(-50%);
  }
  50% {
    transform: translateY(50%);
  }
  100% {
    transform: translateY(-50%);
  }
}

@keyframes moveHorizontal {
  0% {
    transform: translateX(-50%) translateY(-10%);
  }
  50% {
    transform: translateX(50%) translateY(10%);
  }
  100% {
    transform: translateX(-50%) translateY(-10%);
  }
}

.gradient-bg {
  width: 100vw;
  height: 100vh;
  position: absolute;
  overflow: hidden;
  background: linear-gradient(40deg, var(--bg-primary), var(--bg-primary_alt));
  top: 0;
  left: 0;
  opacity: 0.4;
}
.dark .gradient-bg {
  opacity: 1;
}

.gradient-bg svg {
  position: fixed;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
}

.gradient-bg .gradients-container {
  filter: url(#goo) blur(40px);
  width: 100%;
  height: 100%;
}

.gradient-bg .g1 {
  position: absolute;
  background: radial-gradient(circle at center, rgba(var(--color1), 0.8) 0, rgba(var(--color1), 0) 50%) no-repeat;
  mix-blend-mode: var(--blending);
  width: var(--circle-size);
  height: var(--circle-size);
  top: calc(50% - var(--circle-size) / 2);
  left: calc(50% - var(--circle-size) / 2);
  transform-origin: center center;
  animation: moveVertical 30s ease infinite;
  opacity: 1;
}

.gradient-bg .g2 {
  position: absolute;
  background: radial-gradient(circle at center, rgba(var(--color2), 0.8) 0, rgba(var(--color2), 0) 50%) no-repeat;
  mix-blend-mode: var(--blending);
  width: var(--circle-size);
  height: var(--circle-size);
  top: calc(50% - var(--circle-size) / 2);
  left: calc(50% - var(--circle-size) / 2);
  transform-origin: calc(50% - 400px);
  animation: moveInCircle 20s reverse infinite;
  opacity: 1;
}

.gradient-bg .g3 {
  position: absolute;
  background: radial-gradient(circle at center, rgba(var(--color3), 0.8) 0, rgba(var(--color3), 0) 50%) no-repeat;
  mix-blend-mode: var(--blending);
  width: var(--circle-size);
  height: var(--circle-size);
  top: calc(50% - var(--circle-size) / 2 + 200px);
  left: calc(50% - var(--circle-size) / 2 - 500px);
  transform-origin: calc(50% + 400px);
  animation: moveInCircle 40s linear infinite;
  opacity: 1;
}

.gradient-bg .g4 {
  position: absolute;
  background: radial-gradient(circle at center, rgba(var(--color4), 0.8) 0, rgba(var(--color4), 0) 50%) no-repeat;
  mix-blend-mode: var(--blending);
  width: var(--circle-size);
  height: var(--circle-size);
  top: calc(50% - var(--circle-size) / 2);
  left: calc(50% - var(--circle-size) / 2);
  transform-origin: calc(50% - 200px);
  animation: moveHorizontal 40s ease infinite;
  opacity: 0.7;
}

.gradient-bg .g5 {
  position: absolute;
  background: radial-gradient(circle at center, rgba(var(--color5), 0.8) 0, rgba(var(--color5), 0) 50%) no-repeat;
  mix-blend-mode: var(--blending);
  width: calc(var(--circle-size) * 2);
  height: calc(var(--circle-size) * 2);
  top: calc(50% - var(--circle-size));
  left: calc(50% - var(--circle-size));
  transform-origin: calc(50% - 800px) calc(50% + 200px);
  animation: moveInCircle 20s ease infinite;
  opacity: 1;
}

.gradient-bg .interactive {
  position: absolute;
  background: radial-gradient(
      circle at center,
      rgba(var(--color-interactive), 0.8) 0,
      rgba(var(--color-interactive), 0) 50%
    )
    no-repeat;
  mix-blend-mode: var(--blending);
  width: 100%;
  height: 100%;
  top: -50%;
  left: -50%;
  opacity: 0.7;
}
</style>
